// Spend Plan API Service

// Centralized API functions for V2 Spend Plan endpoints.
// All functions return promises and handle authorization headers.

import {
	Account,
	Entity,
	PlanningActivityPayload,
	PlanningActivity,
	ConditionPayload,
	Condition,
	V2ContractPayload,
	V2Contract,
	ExceptionPayload,
	Exception,
	InvoicePayload,
	Invoice,
	SpendPlanPayload,
	SpendPlan,
	VerificationGroup,
	VerificationGroupPayload,
	VoucherPayload,
	Voucher,
	WorkPlanPayload,
	WorkPlan,
} from '../@types';

// Types

export interface ApiResponse<T> {
	data?: T;
	error?: {
		details?: unknown;
		id: string;
		message: string;
	};
	pagination?: {
		page: number;
		perPage: number;
		total: number;
		totalPages: number;
	};
	status: number;
	warning?: {
		details?: unknown;
		id: string;
		message: string;
	};
}

export interface QueryParams {
	fields?: string;
	limit?: number;
	order?: 'ASC' | 'DESC';
	orderBy?: string;
	page?: number;
	populate?: string;
	project?: number;
	[key: string]: number | string | undefined;
}

export interface FetchParams {
	accessToken: string;
	idToken: string;
}

// Helpers

const API_URL = process.env.REACT_APP_API_URL;

// endpoints don't use /v1 prefix - strip it from API_URL if present
const API_URL_V2 = API_URL?.replace(/\/v1\/?$/, '') || API_URL;

const buildQueryString = (params: QueryParams): string => {
	const searchParams = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined) {
			searchParams.append(key, String(value));
		}
	});
	const queryString = searchParams.toString();
	return queryString ? `?${queryString}` : '';
};

const getHeaders = (params: FetchParams): HeadersInit => ({
	Authorization: `Bearer ${params.accessToken}`,
	'Content-Type': 'application/json',
	User: params.idToken,
});

// Activities

export const fetchPlanningActivities = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<PlanningActivity[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/planning-activities${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchActivity = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<PlanningActivity>> => {
	const res = await fetch(`${API_URL_V2}/v2/planning-activities/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createActivity = async (
	params: FetchParams,
	payload: PlanningActivityPayload
): Promise<ApiResponse<PlanningActivity>> => {
	const res = await fetch(`${API_URL_V2}/v2/planning-activities`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateActivity = async (
	params: FetchParams,
	id: number,
	payload: Partial<PlanningActivityPayload>
): Promise<ApiResponse<PlanningActivity>> => {
	const res = await fetch(`${API_URL_V2}/v2/planning-activities/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deactivateActivity = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<PlanningActivity>> => {
	const res = await fetch(`${API_URL_V2}/v2/planning-activities/${id}`, {
		body: JSON.stringify({ status: 'DEACTIVATED' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deleteActivity = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<{ deleted: true }>> => {
	const res = await fetch(`${API_URL_V2}/v2/planning-activities/${id}`, {
		body: JSON.stringify({ action: 'delete' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Contracts

export const fetchContracts = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<V2Contract[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/contracts${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchContract = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<V2Contract>> => {
	const res = await fetch(`${API_URL_V2}/v2/contracts/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createV2Contract = async (
	params: FetchParams,
	payload: V2ContractPayload
): Promise<ApiResponse<V2Contract>> => {
	const res = await fetch(`${API_URL_V2}/v2/contracts`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateV2Contract = async (
	params: FetchParams,
	id: number,
	payload: Partial<V2ContractPayload>
): Promise<ApiResponse<V2Contract>> => {
	const res = await fetch(`${API_URL_V2}/v2/contracts/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deactivateV2Contract = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<V2Contract>> => {
	const res = await fetch(`${API_URL_V2}/v2/contracts/${id}`, {
		body: JSON.stringify({ status: 'DEACTIVATED' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deleteV2Contract = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<{ deleted: true }>> => {
	const res = await fetch(`${API_URL_V2}/v2/contracts/${id}`, {
		body: JSON.stringify({ action: 'delete' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Conditions

export const fetchConditions = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<Condition[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/conditions${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchCondition = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<Condition>> => {
	const res = await fetch(`${API_URL_V2}/v2/conditions/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createCondition = async (
	params: FetchParams,
	payload: ConditionPayload
): Promise<ApiResponse<Condition>> => {
	const res = await fetch(`${API_URL_V2}/v2/conditions`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateCondition = async (
	params: FetchParams,
	id: number,
	payload: Partial<ConditionPayload>
): Promise<ApiResponse<Condition>> => {
	const res = await fetch(`${API_URL_V2}/v2/conditions/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deactivateCondition = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<Condition>> => {
	const res = await fetch(`${API_URL_V2}/v2/conditions/${id}`, {
		body: JSON.stringify({ status: 'DEACTIVATED' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Exceptions

export const fetchExceptions = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<Exception[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/exceptions${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchException = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<Exception>> => {
	const res = await fetch(`${API_URL_V2}/v2/exceptions/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createException = async (
	params: FetchParams,
	payload: ExceptionPayload
): Promise<ApiResponse<Exception>> => {
	const res = await fetch(`${API_URL_V2}/v2/exceptions`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

// Vouchers

export const fetchVouchers = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<Voucher[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/vouchers${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchVoucher = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<Voucher>> => {
	const res = await fetch(`${API_URL_V2}/v2/vouchers/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createVoucher = async (
	params: FetchParams,
	payload: VoucherPayload
): Promise<ApiResponse<Voucher>> => {
	const res = await fetch(`${API_URL_V2}/v2/vouchers`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateVoucher = async (
	params: FetchParams,
	id: number,
	payload: Partial<VoucherPayload>
): Promise<ApiResponse<Voucher>> => {
	const res = await fetch(`${API_URL_V2}/v2/vouchers/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Data (aggregated overviews)

export interface PaymentsOverviewBucket {
	count: number;
	amountsByCurrency: Record<string, number>;
}

export interface PaymentsOverview {
	buckets: {
		AWAITING_REVIEW: PaymentsOverviewBucket;
		AWAITING_PAYMENT: PaymentsOverviewBucket;
		PAID: PaymentsOverviewBucket;
	};
}

export const fetchPaymentsOverview = async (
	params: FetchParams,
	projectId: number
): Promise<ApiResponse<PaymentsOverview>> => {
	const res = await fetch(
		`${API_URL_V2}/v2/data/payments-overview${buildQueryString({ project: projectId })}`,
		{
			headers: getHeaders(params),
			method: 'GET',
		}
	);
	return res.json();
};

// Work Plans

export const fetchWorkPlans = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<WorkPlan[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/work-plans${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchWorkPlan = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<WorkPlan>> => {
	const res = await fetch(`${API_URL_V2}/v2/work-plans/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createWorkPlan = async (
	params: FetchParams,
	payload: WorkPlanPayload
): Promise<ApiResponse<WorkPlan>> => {
	const res = await fetch(`${API_URL_V2}/v2/work-plans`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateWorkPlan = async (
	params: FetchParams,
	id: number,
	payload: Partial<WorkPlanPayload>
): Promise<ApiResponse<WorkPlan>> => {
	const res = await fetch(`${API_URL_V2}/v2/work-plans/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deactivateWorkPlan = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<WorkPlan>> => {
	const res = await fetch(`${API_URL_V2}/v2/work-plans/${id}`, {
		body: JSON.stringify({ status: 'DEACTIVATED' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deleteWorkPlan = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<{ deleted: true }>> => {
	const res = await fetch(`${API_URL_V2}/v2/work-plans/${id}`, {
		body: JSON.stringify({ action: 'delete' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Invoices

export const fetchInvoices = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<Invoice[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/invoices${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchInvoice = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<Invoice[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/invoices/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createInvoice = async (
	params: FetchParams,
	payload: InvoicePayload
): Promise<ApiResponse<Invoice>> => {
	const res = await fetch(`${API_URL_V2}/v2/invoices`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateInvoice = async (
	params: FetchParams,
	id: number,
	payload: Partial<InvoicePayload>
): Promise<ApiResponse<Invoice>> => {
	const res = await fetch(`${API_URL_V2}/v2/invoices/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deactivateInvoice = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<Invoice>> => {
	const res = await fetch(`${API_URL_V2}/v2/invoices/${id}`, {
		body: JSON.stringify({ status: 'DEACTIVATED' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Verification Groups

export const fetchVerificationGroups = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<VerificationGroup[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/verification-groups${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchVerificationGroup = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<VerificationGroup>> => {
	const res = await fetch(`${API_URL_V2}/v2/verification-groups/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createVerificationGroup = async (
	params: FetchParams,
	payload: VerificationGroupPayload
): Promise<ApiResponse<VerificationGroup>> => {
	const res = await fetch(`${API_URL_V2}/v2/verification-groups`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateVerificationGroup = async (
	params: FetchParams,
	id: number,
	payload: Partial<VerificationGroupPayload> & {
		addMembers?: { userId: number; role?: string }[];
		isActive?: boolean;
		removeMembers?: number[];
	}
): Promise<ApiResponse<VerificationGroup>> => {
	const res = await fetch(`${API_URL_V2}/v2/verification-groups/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deactivateVerificationGroup = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<VerificationGroup>> => {
	const res = await fetch(`${API_URL_V2}/v2/verification-groups/${id}`, {
		body: JSON.stringify({ isActive: false }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Accounts

export interface AccountQueryParams {
	fields?: string;
	limit?: number;
	page?: number;
	populate?: string;
	project?: number;
	relationship?: string;
	status?: string;
	type?: string;
	[key: string]: number | string | undefined;
}

export const fetchAccounts = async (
	params: FetchParams,
	query: AccountQueryParams = {}
): Promise<ApiResponse<Account[]>> => {
	const res = await fetch(`${API_URL}/accounts${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchAccount = async (
	params: FetchParams,
	id: number,
	query: AccountQueryParams = {}
): Promise<ApiResponse<Account>> => {
	const res = await fetch(`${API_URL}/accounts/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

// Entities

export const fetchEntities = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<Entity[]>> => {
	const res = await fetch(`${API_URL}/entities${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

// Spend Plans

export const fetchSpendPlans = async (
	params: FetchParams,
	query: QueryParams = {}
): Promise<ApiResponse<SpendPlan[]>> => {
	const res = await fetch(`${API_URL_V2}/v2/spend-plans${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const fetchSpendPlan = async (
	params: FetchParams,
	id: number,
	query: QueryParams = {}
): Promise<ApiResponse<SpendPlan>> => {
	const res = await fetch(`${API_URL_V2}/v2/spend-plans/${id}${buildQueryString(query)}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export const createSpendPlan = async (
	params: FetchParams,
	payload: SpendPlanPayload
): Promise<ApiResponse<SpendPlan>> => {
	const res = await fetch(`${API_URL_V2}/v2/spend-plans`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

export const updateSpendPlan = async (
	params: FetchParams,
	id: number,
	payload: Partial<SpendPlanPayload>
): Promise<ApiResponse<SpendPlan>> => {
	const res = await fetch(`${API_URL_V2}/v2/spend-plans/${id}`, {
		body: JSON.stringify(payload),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

export const deleteSpendPlan = async (
	params: FetchParams,
	id: number
): Promise<ApiResponse<{ deleted: true }>> => {
	const res = await fetch(`${API_URL_V2}/v2/spend-plans/${id}`, {
		body: JSON.stringify({ action: 'delete' }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

// Account Earmark Aggregation

export interface AccountEarmarkSummary {
	accountId: number;
	pendingEarmarked: number;
	finalizedEarmarked: number;
	balance: number;
	available: number;
	plans: {
		id: number;
		name: string;
		status: string;
		totalPlanned: number;
	}[];
}

export const getAccountEarmarks = async (
	params: FetchParams,
	query?: { account?: number; project?: number }
): Promise<ApiResponse<AccountEarmarkSummary[]>> => {
	const searchParams = new URLSearchParams({ aggregate: 'account-earmarks' });
	if (query?.account) searchParams.set('account', String(query.account));
	if (query?.project) searchParams.set('project', String(query.project));
	const res = await fetch(`${API_URL_V2}/v2/spend-plans?${searchParams}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

export interface ActivityEarmark {
	spendPlanId: number;
	spendPlanName: string;
	spendPlanStatus: string;
	accountId: number;
	amountPlanned: number;
	amountVouchered: number;
	remaining: number;
}

export const getActivityEarmarks = async (
	params: FetchParams,
	query: { activity: number; contract?: number }
): Promise<ApiResponse<ActivityEarmark[]>> => {
	const searchParams = new URLSearchParams({
		aggregate: 'activity-earmarks',
		activity: String(query.activity),
	});
	if (query.contract) searchParams.set('contract', String(query.contract));
	const res = await fetch(`${API_URL_V2}/v2/spend-plans?${searchParams}`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};

// Monthly Allocation Grid Types

export interface MonthlyAllocationGridRow {
	activityId: number;
	contractId: number | null;
	accountId: number | null;
	accountName: string | null;
	// hierarchy labels
	activityName: string;
	componentId: number | null;
	componentName: string | null;
	categoryId: number | null;
	categoryName: string | null;
	contractName: string | null;
	// activity metadata (for sub-text display)
	activityAmount: number;
	activityCurrency: string | null;
	activityDateStart: string | null;
	activityDateEnd: string | null;
	// monthly data
	months: Record<string, number>; // { '2026-01-01': 5000, '2026-02-01': 7000 }
	total: number;
}

export interface MonthlyAllocationGrid {
	spendPlanId: number;
	workPlanId: number | null;
	dateStart: string;
	dateEnd: string;
	rows: MonthlyAllocationGridRow[];
	columnTotals: Record<string, number>;
	grandTotal: number;
}

export interface MonthlyAllocationCellUpdate {
	activityId: number;
	contractId?: number | null;
	accountId?: number | null;
	month: string; // 'YYYY-MM-01'
	amount: number;
}

// Monthly Allocation API

export const fetchMonthlyAllocations = async (
	params: FetchParams,
	spendPlanId: number,
	query: QueryParams = {}
): Promise<ApiResponse<MonthlyAllocationGrid>> => {
	const res = await fetch(
		`${API_URL_V2}/v2/spend-plans/${spendPlanId}/monthly${buildQueryString(query)}`,
		{
			headers: getHeaders(params),
			method: 'GET',
		}
	);
	return res.json();
};

export const saveMonthlyAllocations = async (
	params: FetchParams,
	spendPlanId: number,
	allocations: MonthlyAllocationCellUpdate[]
): Promise<ApiResponse<MonthlyAllocationGrid>> => {
	const res = await fetch(`${API_URL_V2}/v2/spend-plans/${spendPlanId}/monthly`, {
		body: JSON.stringify({ allocations }),
		headers: getHeaders(params),
		method: 'PATCH',
	});
	return res.json();
};

/** Deactivate (soft-delete) all monthly rows for a specific account split */
export const deactivateMonthlyAccountSplit = async (
	params: FetchParams,
	spendPlanId: number,
	activityId: number,
	contractId: number | null,
	accountId: number
): Promise<ApiResponse<MonthlyAllocationGrid>> => {
	const res = await fetch(`${API_URL_V2}/v2/spend-plans/${spendPlanId}/monthly`, {
		body: JSON.stringify({ action: 'deactivateSplit', activityId, contractId, accountId }),
		headers: getHeaders(params),
		method: 'POST',
	});
	return res.json();
};

// Work Plan Subtotals

export interface WorkPlanSubtotalItem {
	id: number;
	name: string;
	totalAmount: number;
	currency: string;
	activityCount: number;
}

export interface WorkPlanSubtotals {
	byComponent: WorkPlanSubtotalItem[];
	byCategory: WorkPlanSubtotalItem[];
	byExpenseType: WorkPlanSubtotalItem[];
}

export const fetchWorkPlanSubtotals = async (
	params: FetchParams,
	workPlanId: number
): Promise<ApiResponse<WorkPlanSubtotals>> => {
	const res = await fetch(`${API_URL_V2}/v2/work-plans/${workPlanId}?subtotals=true`, {
		headers: getHeaders(params),
		method: 'GET',
	});
	return res.json();
};
